'use client';

import { Tabs, TabsList, TabsTrigger } from '@/shared/ui/shadcn/tabs';
import { cn } from '@/shared/lib/cn';

interface SegmentOption {
  id: string;
  label: string;
  description?: string;
  icon?: React.ComponentType<{ className?: string }>;
  rightIcon?: React.ComponentType<{ className?: string }>;
}

interface SegmentedControlProps {
  options: SegmentOption[];
  value: string;
  onChange: (value: string) => void;
  className?: string;
  size?: 'sm' | 'md' | 'lg';
  fullWidth?: boolean;
}

export function SegmentedControl({
  options,
  value,
  onChange,
  className = '',
  size = 'md',
  fullWidth = false,
}: SegmentedControlProps) {
  const sizeClasses = {
    sm: 'h-8',
    md: 'h-10',
    lg: 'h-12',
  };

  const triggerSizeClasses = {
    sm: 'px-3 py-1 text-form-help',
    md: 'px-4 py-2 text-card-body md:text-form-help',
    lg: 'px-6 py-3 text-card-title md:text-card-body',
  };

  return (
    <Tabs
      value={value}
      onValueChange={onChange}
      className={cn(fullWidth ? 'w-full' : '', className)}
    >
      <TabsList
        className={cn(
          // iOS-style segmented control: filled neutral background, no outer border,
          // selected segment slides over as a white pill with a subtle shadow.
          'rounded-full bg-muted p-1 gap-0',
          sizeClasses[size],
          fullWidth ? 'w-full' : 'w-auto'
        )}
      >
        {options.map((option) => {
          const Icon = option.icon;
          const RightIcon = option.rightIcon;

          return (
            <TabsTrigger
              key={option.id}
              value={option.id}
              className={cn(
                'rounded-full text-muted-foreground transition-colors data-[state=active]:bg-segment-active data-[state=active]:shadow-sm data-[state=active]:text-foreground data-[state=active]:font-medium',
                triggerSizeClasses[size],
                fullWidth ? 'flex-1' : ''
              )}
            >
              {Icon && <Icon className="w-4 h-4 me-2" />}
              <div className="flex flex-col items-center">
                <div className="flex items-center gap-1">
                  <span>{option.label}</span>
                  {RightIcon && <RightIcon className="w-3 h-3" />}
                </div>
                {option.description && (
                  <span className="text-card-meta opacity-70 mt-0.5">
                    {option.description}
                  </span>
                )}
              </div>
            </TabsTrigger>
          );
        })}
      </TabsList>
    </Tabs>
  );
}
